import { useEffect, useState } from 'react'
import { ArrowRight, Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Separator } from '@/shared/ui/separator'
import { t } from '@/shared/lib/i18n'
import apiClient from '@/shared/lib/apiClient'
import { instrumentReportsDepartmentMovesByDatePath } from '@/shared/lib/routes'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

interface DepartmentMove {
  id: number
  oldDepartmentName: string | null
  newDepartmentName: string | null
  oldUnitName: string | null
  newUnitName: string | null
  changedByName: string | null
  changedAt: string
  comments: string | null
}

interface Props {
  instrumentId: number
  date: string | null
  onClose: () => void
}

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; records: DepartmentMove[] }

const InstrumentDepartmentMovesDialog = ({ instrumentId, date, onClose }: Props) => {
  const { formatDateTime } = useSiteDateTime()
  const [loadState, setLoadState] = useState<LoadState>({ status: 'idle' })

  const fetchRecords = (targetDate: string) => {
    setLoadState({ status: 'loading' })
    apiClient
      .get<{ departmentMoves: DepartmentMove[] }>(
        instrumentReportsDepartmentMovesByDatePath(instrumentId, { date: targetDate }),
      )
      .then((res) => setLoadState({ status: 'loaded', records: res.data.departmentMoves }))
      .catch(() => setLoadState({ status: 'error' }))
  }

  useEffect(() => {
    if (date) fetchRecords(date)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentId, date])

  const isOpen = date != null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('instruments.reports.departmentMoves.dialogTitle')}</DialogTitle>
          {date && <p className="text-sm text-muted-foreground">{date}</p>}
        </DialogHeader>

        <Separator />

        {loadState.status === 'loading' && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {loadState.status === 'error' && (
          <p className="py-4 text-center text-sm text-destructive">
            {t('instruments.reports.logDetails.error')}
          </p>
        )}

        {loadState.status === 'loaded' && loadState.records.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {t('instruments.reports.dialogs.empty')}
          </p>
        )}

        {loadState.status === 'loaded' && loadState.records.length > 0 && (
          <div className="space-y-3">
            {loadState.records.map((move) => (
              <div key={move.id} className="rounded border p-3 text-sm space-y-1">
                <div className="flex items-center gap-2 text-xs font-medium">
                  <span className="text-muted-foreground">
                    {[move.oldDepartmentName, move.oldUnitName].filter(Boolean).join(' / ') || '—'}
                  </span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span>
                    {[move.newDepartmentName, move.newUnitName].filter(Boolean).join(' / ') || '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatDateTime(move.changedAt)}</span>
                  {move.changedByName && <span>{move.changedByName}</span>}
                </div>
                {move.comments && <p className="text-xs">{move.comments}</p>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default InstrumentDepartmentMovesDialog
